import { useState } from 'react'
import Modal from '../components/Modal'
import Icon from '../components/Icon'
import { ErrorText, Field, Row, Tags, Text, TextArea } from '../components/Form'
import { saveContact, deleteContact } from '../lib/mutations'
import type { ContactDraft } from '../lib/mutations'
import { toDateInputValue } from '../lib/zonedTime'
import type { Contact, WithId } from '../types/models'

/**
 * Add or edit a contact.
 *
 * The everyday fields are on the surface; passport, next of kin and medical
 * detail sit behind a disclosure. A tour manager fills those in once a year for
 * a visa run, and confronting them with a passport form every time they add a
 * promoter's phone number is how the address book stops getting used.
 */
export default function ContactDialog({
  open,
  contact,
  ownerId,
  onClose,
}: {
  open: boolean
  contact?: WithId<Contact> | null
  ownerId: string
  onClose: () => void
}) {
  const [draft, setDraft] = useState<ContactDraft>(() => initialDraft(contact))
  const [showTravel, setShowTravel] = useState(() => hasTravelDetail(contact))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)

  const patch = (next: Partial<ContactDraft>) => setDraft((previous) => ({ ...previous, ...next }))
  const valid = draft.name.trim().length > 0

  const passportStarted =
    !!draft.passportNumber || !!draft.passportCountry || !!draft.passportIssued || !!draft.passportExpiry
  const passportComplete =
    !!draft.passportNumber?.trim() &&
    !!draft.passportCountry?.trim() &&
    !!draft.passportIssued &&
    !!draft.passportExpiry

  async function save() {
    if (!valid) return
    setBusy(true)
    setError(null)
    try {
      await saveContact(draft, ownerId, contact?.id)
      onClose()
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      title={contact ? 'Edit contact' : 'New contact'}
      width={560}
      onClose={onClose}
      footer={
        <>
          {contact && (
            <button
              className="btn btn-danger"
              style={{ marginRight: 'auto' }}
              disabled={busy}
              onClick={() => setConfirming(true)}
            >
              Delete
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={!valid || busy}>
            {busy ? 'Saving…' : 'Save contact'}
          </button>
        </>
      }
    >
      <div className="stack" style={{ gap: 16 }}>
        <section className="stack">
          <Field label="Name">
            <Text value={draft.name} onChange={(value) => patch({ name: value })} autoFocus />
          </Field>

          <Field label="Roles" hint="Promoter, FOH engineer, driver — whatever you'd search for.">
            <Tags value={draft.roles} onChange={(value) => patch({ roles: value })} />
          </Field>

          <Row>
            <Field label="Email">
              <Text
                type="email"
                value={draft.email ?? ''}
                onChange={(value) => patch({ email: value })}
              />
            </Field>
            <Field label="Phone">
              <Text value={draft.phone ?? ''} onChange={(value) => patch({ phone: value })} />
            </Field>
          </Row>

          <Field label="Based in">
            <Text
              value={draft.location ?? ''}
              onChange={(value) => patch({ location: value })}
            />
          </Field>

          <Field label="Notes">
            <TextArea value={draft.notes ?? ''} onChange={(value) => patch({ notes: value })} />
          </Field>
        </section>

        <button
          className="row btn btn-secondary"
          style={{ justifyContent: 'space-between' }}
          onClick={() => setShowTravel((current) => !current)}
        >
          <span>Travel &amp; emergency details</span>
          <Icon name={showTravel ? 'chevronDown' : 'chevronRight'} size={15} />
        </button>

        {showTravel && (
          <section className="stack" style={{ gap: 14 }}>
            <div className="stack">
              <span className="t-eyebrow">Passport</span>
              <Row>
                <Field label="Number">
                  <Text
                    value={draft.passportNumber ?? ''}
                    onChange={(value) => patch({ passportNumber: value.toUpperCase() })}
                  />
                </Field>
                <Field label="Issuing country">
                  <Text
                    value={draft.passportCountry ?? ''}
                    onChange={(value) => patch({ passportCountry: value })}
                  />
                </Field>
              </Row>
              <Row>
                <Field label="Issued">
                  <Text
                    type="date"
                    value={draft.passportIssued ?? ''}
                    onChange={(value) => patch({ passportIssued: value })}
                  />
                </Field>
                <Field label="Expires">
                  <Text
                    type="date"
                    value={draft.passportExpiry ?? ''}
                    onChange={(value) => patch({ passportExpiry: value })}
                  />
                </Field>
              </Row>
              {passportStarted && !passportComplete && (
                <p className="t-caption" style={{ color: 'var(--warning)' }}>
                  A passport needs all four fields to be saved — a partial one can't be read back.
                </p>
              )}
            </div>

            <div className="stack">
              <span className="t-eyebrow">Emergency contact</span>
              <Row>
                <Field label="Name">
                  <Text
                    value={draft.emergencyName ?? ''}
                    onChange={(value) => patch({ emergencyName: value })}
                  />
                </Field>
                <Field label="Phone">
                  <Text
                    value={draft.emergencyPhone ?? ''}
                    onChange={(value) => patch({ emergencyPhone: value })}
                  />
                </Field>
              </Row>
            </div>

            <Row>
              <Field label="Allergies">
                <Text
                  value={draft.allergies ?? ''}
                  onChange={(value) => patch({ allergies: value })}
                />
              </Field>
              <Field label="Medications">
                <Text
                  value={draft.medications ?? ''}
                  onChange={(value) => patch({ medications: value })}
                />
              </Field>
            </Row>

            <LoyaltyEditor
              programs={draft.loyaltyPrograms ?? []}
              onChange={(loyaltyPrograms) => patch({ loyaltyPrograms })}
            />
          </section>
        )}

        <ErrorText>{error}</ErrorText>

        {confirming && contact && (
          <div
            className="stack"
            style={{
              gap: 8,
              padding: 14,
              borderRadius: 'var(--radius-control)',
              background: 'color-mix(in srgb, var(--destructive) 10%, transparent)',
            }}
          >
            <strong>Delete {contact.name}?</strong>
            <p className="t-caption">
              They come out of your address book. Crew records on tours that already reference
              them are unaffected.
            </p>
            <div className="row" style={{ gap: 8 }}>
              <button
                className="btn btn-danger"
                disabled={busy}
                onClick={async () => {
                  setBusy(true)
                  try {
                    await deleteContact(contact.id)
                    onClose()
                  } catch (caught) {
                    setError((caught as Error).message)
                  } finally {
                    setBusy(false)
                  }
                }}
              >
                {busy ? 'Deleting…' : 'Delete contact'}
              </button>
              <button className="btn btn-quiet" onClick={() => setConfirming(false)}>
                Keep it
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

function LoyaltyEditor({
  programs,
  onChange,
}: {
  programs: Array<{ id: string; airline: string; accountNumber: string }>
  onChange: (next: Array<{ id: string; airline: string; accountNumber: string }>) => void
}) {
  return (
    <div className="stack" style={{ gap: 8 }}>
      <span className="t-eyebrow">Frequent flyer</span>
      {programs.length > 0 && (
        // Column headers, so the two inputs are labelled without grey example
        // text sitting inside them looking like a value.
        <div className="row" style={{ gap: 8 }}>
          <span className="t-eyebrow grow">Airline</span>
          <span className="t-eyebrow grow">Account number</span>
          <span style={{ width: 34 }} />
        </div>
      )}

      {programs.map((program, index) => (
        <div key={program.id} className="row" style={{ gap: 8 }}>
          <input
            className="field"
            aria-label="Airline"
            value={program.airline}
            onChange={(event) => {
              const next = [...programs]
              next[index] = { ...program, airline: event.target.value }
              onChange(next)
            }}
          />
          <input
            className="field"
            aria-label="Account number"
            value={program.accountNumber}
            onChange={(event) => {
              const next = [...programs]
              next[index] = { ...program, accountNumber: event.target.value }
              onChange(next)
            }}
          />
          <button
            aria-label="Remove"
            title="Remove"
            style={{ color: 'var(--text-tertiary)', padding: 6 }}
            onClick={() => onChange(programs.filter((entry) => entry.id !== program.id))}
          >
            <Icon name="close" size={15} />
          </button>
        </div>
      ))}

      <button
        className="btn btn-quiet"
        style={{ alignSelf: 'flex-start', paddingLeft: 0 }}
        onClick={() =>
          onChange([
            ...programs,
            { id: crypto.randomUUID(), airline: '', accountNumber: '' },
          ])
        }
      >
        <Icon name="plus" size={14} /> Add a programme
      </button>
    </div>
  )
}

function hasTravelDetail(contact?: WithId<Contact> | null): boolean {
  if (!contact) return false
  return !!(
    contact.passport ||
    contact.emergencyContact ||
    contact.allergies ||
    contact.medications ||
    contact.loyaltyPrograms?.length
  )
}

function initialDraft(contact?: WithId<Contact> | null): ContactDraft {
  if (!contact) {
    return { name: '', roles: [], loyaltyPrograms: [] }
  }
  return {
    name: contact.name,
    roles: contact.roles ?? [],
    email: contact.email ?? '',
    phone: contact.phone ?? '',
    location: contact.location ?? '',
    notes: contact.notes ?? '',
    countryOfBirth: contact.countryOfBirth ?? '',
    allergies: contact.allergies ?? '',
    medications: contact.medications ?? '',
    emergencyName: contact.emergencyContact?.name ?? '',
    emergencyPhone: contact.emergencyContact?.phone ?? '',
    passportNumber: contact.passport?.passportNumber ?? '',
    passportCountry: contact.passport?.issuingCountry ?? '',
    passportIssued: contact.passport
      ? toDateInputValue(contact.passport.issuedDate.toDate())
      : '',
    passportExpiry: contact.passport
      ? toDateInputValue(contact.passport.expiryDate.toDate())
      : '',
    loyaltyPrograms: (contact.loyaltyPrograms ?? []).map((entry) => ({
      id: entry.id || crypto.randomUUID(),
      airline: entry.airline,
      accountNumber: entry.accountNumber,
    })),
  }
}
